import stylesheet from './FileUpload.scss?inline';
import {NXTInputField} from './InputField';
import {getIconStyles} from '../../util/lazy';
import '../icon/icon';
import '../button/button';

interface FileEntry {
    file: File;
    uploading: boolean;
}

export class NXTFileUpload extends NXTInputField<File[]> {
    private fileInput: HTMLInputElement;
    private fileList: HTMLUListElement;
    private entries: FileEntry[] = [];

    constructor() {
        super();
        this.customStyle = stylesheet;
        void this.appendStyleLazy(getIconStyles);
    }

    get multiple(): boolean {
        return this.fileInput?.multiple ?? this.hasAttribute('multiple');
    }

    get accept(): string | null {
        return this.getAttribute('accept');
    }

    get disabled(): boolean {
        return this.hasAttribute('disabled');
    }

    get labelRemove(): string {
        return this.getAttribute('label-remove') ?? 'Remove file';
    }

    get size(): string {
        return this.getAttribute('size') ?? 'md';
    }

    public get value(): File[] {
        return this.entries.map(entry => entry.file);
    }

    public setUploading(index: number, uploading: boolean) {
        if (this.entries[index]) {
            this.entries[index].uploading = uploading;
            this.renderFileList();
        }
    }

    private syncNativeInput() {
        const transfer = new DataTransfer();
        this.entries.forEach(entry => transfer.items.add(entry.file));
        this.fileInput.files = transfer.files;
    }

    private addFiles(files: FileList, uploading = false) {
        const incoming = Array.from(files).map(file => ({file, uploading}));
        this.entries = this.multiple ? [...this.entries, ...incoming] : incoming.slice(-1);
        this.onFilesChanged();
    }

    private removeAt(index: number) {
        this.entries.splice(index, 1);
        this.onFilesChanged();
    }

    private onFilesChanged() {
        this.syncNativeInput();
        this.renderFileList();
        this.dispatchEvent(new Event('change', {bubbles: true}));
    }

    private renderFileList() {
        this.fileList.innerHTML = '';
        this.entries.forEach((entry, index) => {
            const item = document.createElement('li');
            item.classList.add('file-item');

            const name = document.createElement('span');
            name.classList.add('file-name');
            name.textContent = entry.file.name;
            item.appendChild(name);

            if (entry.uploading) {
                const spinner = document.createElement('span');
                spinner.classList.add('spinner');
                item.appendChild(spinner);
            } else {
                const remove = document.createElement('button');
                remove.type = 'button';
                remove.classList.add('remove');
                remove.setAttribute('aria-label', this.labelRemove);
                remove.disabled = this.disabled;
                remove.innerHTML = `<nxt-icon icon="close"></nxt-icon>`;
                remove.addEventListener('click', () => this.removeAt(index));
                item.appendChild(remove);
            }

            this.fileList.appendChild(item);
        });
    }

    protected renderControl(): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('file-upload');
        container.classList.toggle('disabled', this.disabled);

        const button = document.createElement('nxt-button');
        button.classList.add('upload-button');
        button.setAttribute('type', 'primary');
        button.setAttribute('icon-right', 'upload');
        button.setAttribute('block', '');
        button.setAttribute('size', this.size);
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName)) {
                button.setAttribute(attributeName, this.getAttribute(attributeName));
            }
        }
        if (this.disabled) {
            button.setAttribute('disabled', '');
        }
        button.innerHTML = `<slot name="label-upload">Upload</slot>`;

        const slottedInputs = this.getSlotElements('input').filter(
            (el): el is HTMLInputElement => el instanceof HTMLInputElement,
        );

        if (slottedInputs.length > 0) {
            this.fileInput = slottedInputs[0];
        } else {
            this.fileInput = document.createElement('input');
            this.fileInput.type = 'file';
            if (this.hasAttribute('name')) {
                this.fileInput.name = this.getAttribute('name');
            }
            this.fileInput.required = this.hasAttribute('required');
            this.fileInput.multiple = this.hasAttribute('multiple');
            this.fileInput.disabled = this.disabled;
            if (this.accept) {
                this.fileInput.accept = this.accept;
            }
            this.fileInput.setAttribute('slot', 'input');
            this.appendChild(this.fileInput);
        }

        this.fileInput.classList.add('native-input');
        this.fileInput.addEventListener('change', () => {
            if (this.fileInput.files?.length) {
                this.addFiles(this.fileInput.files);
            }
        });

        button.addEventListener('click', () => this.fileInput.click());

        const slot = document.createElement('slot');
        slot.setAttribute('name', 'input');

        this.fileList = document.createElement('ul');
        this.fileList.classList.add('file-list');

        container.append(button, slot, this.fileList);
        return container;
    }
}

customElements.define('nxt-file-upload', NXTFileUpload);
